import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Sparkles, Zap } from 'lucide-react';
interface SlideAIEconomicsProps {
  onContinue: () => void;
}
export function SlideAIEconomics({
  onContinue
}: SlideAIEconomicsProps) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 400);
    const t2 = setTimeout(() => setStage(2), 1200);
    const t3 = setTimeout(() => setStage(3), 2000);
    const t4 = setTimeout(() => setStage(4), 2900);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);
  return <div className="h-full w-full flex flex-col items-center px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-101">
      {/* Sparkle icon */}
      <motion.div initial={{
      opacity: 0,
      scale: 0.8
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      scale: stage >= 1 ? 1 : 0.8
    }} transition={{
      duration: 0.5
    }} className="mb-6" data-id="element-102">
        <div className="w-14 h-14 rounded-2xl bg-teal-400/10 border border-teal-400/30 flex items-center justify-center" data-id="element-103">
          <Sparkles className="w-6 h-6 text-teal-400" data-id="element-104" />
        </div>
      </motion.div>

      {/* Heading */}
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} transition={{
      duration: 0.6,
      delay: 0.2
    }} className="text-4xl md:text-6xl font-serif text-white text-center leading-tight max-w-4xl" data-id="element-105">
        AI changes the
        <br data-id="element-106" />
        <span className="bg-gradient-to-r from-blue-400 via-teal-400 to-teal-300 bg-clip-text text-transparent" data-id="element-107">
          campaign economics
        </span>
      </motion.h2>

      {/* Cards */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch mt-12 w-full max-w-5xl" data-id="element-108">
        {/* The Promise — muted */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: stage >= 2 ? 1 : 0,
        y: stage >= 2 ? 0 : 30
      }} transition={{
        duration: 0.5
      }} className="bg-navy-800/60 border border-navy-600 rounded-2xl p-8 flex flex-col" data-id="element-109">
          <h3 className="text-2xl font-semibold text-white/70 mb-6" data-id="element-110">
            The Promise
          </h3>
          <p className="text-white/50 leading-relaxed text-lg" data-id="element-111">
            Generative AI drastically reduces the cost of content creation,
            making it{' '}
            <span className="text-white/70 italic" data-id="element-112">theoretically</span> possible
            to support the long tail.
          </p>
        </motion.div>

        {/* The Reality — highlighted */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: stage >= 3 ? 1 : 0,
        y: stage >= 3 ? 0 : 30
      }} transition={{
        duration: 0.5
      }} className="bg-navy-700/60 border border-teal-400/40 rounded-2xl p-8 flex flex-col relative overflow-hidden shadow-[0_0_40px_rgba(0,212,170,0.08)]" data-id="element-113">
          {/* Subtle inner glow */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-teal-400/10 rounded-full blur-3xl pointer-events-none" data-id="element-114" />

          <div className="flex items-center gap-3 mb-6 relative z-10" data-id="element-115">
            <div className="w-9 h-9 rounded-lg bg-amber-400/15 border border-amber-400/30 flex items-center justify-center" data-id="element-116">
              <Zap className="w-4 h-4 text-amber-400" data-id="element-117" />
            </div>
            <h3 className="text-2xl font-semibold text-white" data-id="element-118">The Reality</h3>
          </div>
          <div className="space-y-5 relative z-10" data-id="element-119">
            <p className="text-white text-lg leading-relaxed font-medium" data-id="element-120">
              AI alone is{' '}
              <span className="text-teal-400 font-semibold" data-id="element-121">not enough.</span>
            </p>
            <p className="text-white/85 text-lg leading-relaxed" data-id="element-122">
              Execution is the bottleneck. You need{' '}
              <span className="text-white font-semibold" data-id="element-123">
                strategy, compliance, distribution, and measurement.
              </span>
            </p>
          </div>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 4 ? 1 : 0
    }} className="mt-8 flex justify-end w-full max-w-5xl" data-id="element-124">
        <Button onClick={onContinue} data-id="element-125">Differentiation</Button>
      </motion.div>
    </div>;
}